import { Link, useParams } from "@tanstack/react-router";
import { NewBadge } from "../../../shared/ui/new-badge";
import { useTranslation } from "react-i18next";
import { isStayItem, primaryCuisine, showsActivityPrice } from "@majourneys/shared";
import { CardImageCarousel } from "../../../shared/ui/card-image-carousel";
import { useDisplayPriceParts } from "../../../lib/currency";
import { activityHrefParts } from "../lib/activity-href";
import { cardPriceUnitKey } from "../lib/price-unit";
import { useRecentBooking } from "../lib/recent-booking";
import { useSaved } from "../../saved/state/saved-context";

import { photoFrameStyle, photoInnerStyle } from "../../../shared/ui/card-frame";

import { Icon } from "@majourneys/icons";
export interface ActivityCardDiscoverProps {
  slug: string;
  /**
   * Single photo. Kept for callers that genuinely have one (a card built from
   * a search hit that carries no gallery); `images` wins when both are given.
   */
  image?: string;
  /**
   * The activity's photos, in the order the API returned them. The list
   * endpoints already send every stable URL they hold, so a card that shows
   * only the first was throwing away photos it had been paid for.
   */
  images?: string[];
  title: string;
  city: string;
  /** Canonical city slug — required for the 4-segment SEO URL. */
  citySlug?: string | null;
  category?: string;
  /** Canonical category slug — required for bookable rows' SEO URL. */
  categorySlug?: string | null;
  /** Canonical segment for explore items (when no category). */
  segment?: string | null;
  /** "bookable" | "explore" — drives whether segment or category wins. */
  mode?: string;
  /** Venue tags: a restaurant's cuisine replaces the category word on the card. */
  tags?: readonly string[] | null;
  /** The cuisine chip the guest picked: named when this venue has it. */
  preferCuisine?: string | null;
  /**
   * The list is one city, so a cuisine card leaves the city out: on a phone
   * "Méditerranéenne · Marrakech" left "Marrakech" alone on a second line,
   * and the page already says which city it is.
   */
  oneCity?: boolean;
  price: number;
  currency?: string;
  rating?: string | number | null;
  isBookable?: boolean;
  typePrice?: number;
  priceType?: string | number;
  /**
   * Badge a non-bookable row "Not bookable". Only the Restaurants and Spas
   * hubs ask for it, where bookable and guide-only venues share one grid
   * (owner, 2026-10-07). A page of attractions would wear it on every card.
   */
  markUnbookable?: boolean;
  /**
   * Search the card's links carry, so the venue's booking sheet opens filled
   * in (`?book=1&date=&time=&adults=` from the category page's booking bar).
   * Bookable rows only: an explore row has no booking sheet.
   */
  linkSearch?: Record<string, string | number>;
  /** Added in the last 30 days (decided by the API): a gold "New" pill on the photo. */
  isNew?: boolean;
}

const POPPINS = "var(--font-sans)";

export function ActivityCardDiscover({
  slug,
  image,
  images,
  title,
  city,
  citySlug,
  category,
  categorySlug,
  segment,
  mode,
  tags,
  preferCuisine,
  oneCity = false,
  price,
  currency = "MAD",
  markUnbookable = false,
  rating,
  isBookable = true,
  typePrice,
  priceType,
  linkSearch,
  isNew = false,
}: ActivityCardDiscoverProps) {
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const { t } = useTranslation();
  const cuisine = preferCuisine && tags?.includes(preferCuisine) ? preferCuisine : primaryCuisine(tags);
  const { isSaved, toggle: toggleSaved } = useSaved();
  const saved = isSaved(slug);
  const locale = lang ?? "en";
  const display = useDisplayPriceParts()(price);
  const recentBookingRef = useRecentBooking(slug);
  const isExplore = mode === "explore";
  // An explore row is never bookable, whatever the caller passed. `ActivityGrid`
  // forwards `mode` but not `isBookable`, which defaults to true — so once the
  // segment hubs started serving explore rows, every restaurant and attraction
  // card rendered a "Book now" button and a "MAD 0" price for something that
  // cannot be reserved and has no price. Deriving it here rather than at each
  // call site means a new caller cannot reintroduce that.
  const bookable = isBookable && !isExplore;
  // A bookable row can still have no price to show. Restaurants are the case:
  // you reserve a table for free and pay for whatever you order on the night,
  // so the package carries a 0 and the card must stay quiet about it rather
  // than advertise "MAD 0". The show page, the preview sheet and the similar-
  // listings rail all already guard on `price > 0`; the card was the one place
  // that did not.
  // And never on a restaurant, even when old package rows still hold a price.
  const showPrice = bookable && showsActivityPrice({ price, mode, categorySlug, segment });
  const showBookedCta = bookable && !!recentBookingRef;
  const shortRef =
    recentBookingRef && recentBookingRef.length > 12
      ? `${recentBookingRef.slice(0, 7)}…${recentBookingRef.slice(-4)}`
      : recentBookingRef;

  // Always link to the canonical 4-segment URL — never the legacy short
  // /<lang>/<slug>, which would force a 301 hop on every internal click.
  // activityHrefParts falls back to "morocco" / "place" when slugs are
  // missing so we still produce a valid (non-redirecting from app POV)
  // route.
  // `images` when the caller has the gallery, the single `image` otherwise.
  // An empty array is not a gallery: it would drop a photo the caller did
  // pass as `image`.
  const photos = images && images.length > 0 ? images : image ? [image] : [];
  const isStay = isStayItem(category, segment, typePrice ?? priceType);
  const linkParts = activityHrefParts({
    lang: locale,
    citySlug: citySlug ?? null,
    categorySlug: categorySlug ?? null,
    segment: isExplore ? segment ?? null : null,
    slug,
  });
  const carried = bookable && linkSearch ? linkSearch : undefined;

  return (
    <article
      /*
        The Discover card's shape: the photo wears the pewter frame on its own
        and the text sits on the page beneath it, no panel round the whole card.
        `h-full` keeps the row even. The spare height falls below the price,
        never under the title: a one-line title left a blank line between it
        and the category line.
      */
      className="group relative flex h-full w-full flex-col transition-transform duration-200 ease-out hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <div className="mj-photo-frame relative" style={photoFrameStyle}>
      <div className="mj-photo-frame-inner overflow-hidden" style={photoInnerStyle}>
      {/* The photos flip in place; each is still a router Link, and only the
          first is a tab stop so a keyboard user does not walk through eight
          copies of the same destination to reach the price. */}
      <CardImageCarousel
        images={photos}
        alt={title}
        className="aspect-[4/5] w-full overflow-hidden"
        width={480}
        height={600}
        arrowsOnPhone={false}
        renderSlide={(photo, i) => (
          <Link
            to={linkParts.to}
            params={linkParts.params}
            search={carried as never}
            className="block h-full w-full"
            tabIndex={i === 0 ? undefined : -1}
          >
            {photo}
          </Link>
        )}
        overlay={
          <>
          {markUnbookable && mode === "explore" ? (
            <span className="pointer-events-none absolute start-[10px] top-[10px] z-10 inline-flex min-h-[26px] items-center whitespace-nowrap rounded-full bg-white/95 px-3 text-[10px] font-bold uppercase leading-[14px] tracking-[0.08em] text-[#110E39] shadow-[0_2px_8px_rgba(4,0,51,0.15)]">
              {t("activity.not_bookable")}
            </span>
          ) : null}
          {isNew ? (
            /* Above the photo dots when there are several photos: at the dots'
               height a long word ("NOUVEAU") runs into them. */
            <NewBadge
              className={`absolute start-[10px] z-10 ${
                photos.filter((p) => p && p.trim() !== "").length > 1 ? "bottom-[24px]" : "bottom-[10px]"
              }`}
            />
          ) : null}
          {/*
            White outline on the photo, as on Discover: no disc behind it, a
            drop shadow keeps it legible over a pale sky. The button stays 36px
            so the hit area is unchanged.
          */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void toggleSaved({ slug, title, city, price, currency, priceType, image: photos[0], category });
            }}
            aria-pressed={saved}
            aria-label={saved ? t("activity.unsave") : t("activity.save")}
            data-testid="activity-card-save"
            className={`absolute end-[4px] top-[4px] z-10 inline-flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
              saved ? "text-[#E5B265]" : "text-white"
            }`}
          >
            <Icon
              name={saved ? "ri-heart-fill" : "ri-heart-line"}
              className="text-[26px] leading-none drop-shadow-[0_1px_3px_rgba(4,0,51,0.45)]"
              aria-hidden="true"
            />
          </button>
          </>
        }
      />
      </div>
      </div>

      <div className="flex flex-1 flex-col px-1 pb-[2px] pt-[10px]" style={{ fontFamily: POPPINS }}>
        <h3
          className="m-0 overflow-hidden text-[14px] font-bold leading-[1.3] [overflow-wrap:anywhere] min-[400px]:text-[15px] sm:text-[15.5px]"
          style={{
            display: "-webkit-box",
            // Three lines on a phone, where two cards share a row and a
            // title like "Wakeboarding in Marrakech" needs the room.
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
          title={title}
        >
          <Link
            to={linkParts.to}
            params={linkParts.params}
            search={carried as never}
            className="text-[#110E39] no-underline"
          >
            {title}
          </Link>
        </h3>

        <p className="m-0 mt-[2px] text-[12px] font-medium leading-[1.35] text-[#5a5d77] min-[400px]:text-[13px]">
          {/* "Moroccan · Marrakech" rather than "Restaurants · Marrakech": the
              cuisine says more than the category the page already names
              (owner, 2026-10-31). Anything without a cuisine keeps its category. */}
          {(cuisine ? [t(`restaurant_cuisines.${cuisine}`), oneCity ? null : city] : [category, city])
            .filter(Boolean)
            .join(" · ")}
        </p>

        {showPrice ? (
          <p className="m-0 mt-[1px] text-[12.5px] font-bold leading-[1.35] text-[#110E39] min-[400px]:text-[13px]">
            {/* A camp is a stay: same "From … per night" line as a hotel card. */}
            {isStay ? <span className="font-medium text-[#5a5d77]">{t("activity.from")} </span> : null}
            {display.value} {display.currency}{" "}
            <span className="whitespace-nowrap font-medium text-[#5a5d77]">
              {t(
                cardPriceUnitKey({
                  category,
                  categorySlug,
                  slug,
                  priceType: typePrice ?? priceType,
                  isStay,
                }),
              )}
            </span>
          </p>
        ) : null}

        {showBookedCta && recentBookingRef ? (
          <Link
            to="/$lang/booking/$bookingId/thank-you"
            params={{ lang: locale, bookingId: recentBookingRef }}
            aria-label={t("booking_sheet.view_booking", { defaultValue: "View my booking" })}
            className="mt-[8px] flex w-full min-h-[38px] items-center justify-between gap-2 rounded-full bg-gradient-to-r from-[#110E39] to-[#241d6e] px-2 ps-2.5 text-white no-underline shadow-[0_8px_18px_rgba(17,14,57,0.25)] transition-opacity duration-150 hover:opacity-95"
          >
            <span className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E5B265]">
                <Icon name="ri-check-line" className="text-[10px] text-[#110E39]" aria-hidden />
              </span>
              <span className="leading-tight">
                <span className="block text-[8.5px] font-bold uppercase tracking-[0.12em] text-[#E5B265]">
                  {t("activity.timeline_awaiting", { defaultValue: "Awaiting" })}
                </span>
                <span className="block font-mono text-[11px] text-white/90">{shortRef}</span>
              </span>
            </span>
            <Icon name="ri-arrow-right-line" className="text-[14px] rtl:-scale-x-100" aria-hidden />
          </Link>
        ) : null}
      </div>
    </article>
  );
}
